import { use } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AccountTreeOutlined, CheckRounded } from '@mui/icons-material';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import { Button, Typography } from '@mui/material';
import Constants from 'style/constants';

import FeatureSubscriptionRow from 'features/feature-subscriptions/components/feature-subscription-row/feature-subscription-row';
import LocationFeatureSubscriptionsProviderContext from 'features/feature-subscriptions/contexts/location-feature-subscriptions-provider-context';
import TenantFeatureSubscriptionsProviderContext from 'features/feature-subscriptions/contexts/tenant-feature-subscriptions-provider-context';
import useFeatureSubscriptionFeatureFlag from 'features/feature-subscriptions/hooks/feature-subscription-feature-flag';
import { FeatureSubscriptionType } from 'features/feature-subscriptions/models/feature-subscription-type';
import styles from './tenant-feature-subscriptions.module.scss';

const TenantFeatureSubscriptions = (): React.JSX.Element => {
  const { t } = useTranslation('featureSubscriptions');

  const {
    viewingMode,
    activeEditor,
    form,
    readTenantFeatureSubscriptionsData,
    readTenantFeatureSubscriptionsIsSuccess,
    handleOnEdit,
    handleOnCancel,
    submitTenantFeatureSubscriptions,
  } = use(TenantFeatureSubscriptionsProviderContext);

  const { isFeatureFlagForSubscriptionEnabled } = useFeatureSubscriptionFeatureFlag();

  const { activeLocationFeatureSubscriptionTypes } = use(LocationFeatureSubscriptionsProviderContext);

  const isEditing = viewingMode === 'editing';
  const isLocationEditor = activeEditor === 'location';

  const renderHeaderRow = (): React.JSX.Element => (
    <div className={styles.headerRow}>
      <AccountTreeOutlined />
      <Typography variant="h5" className={styles.title} sx={{ color: Constants.Colors.onSurfaceVariant }}>
        {t('organisationSubscriptions.header')}
      </Typography>
      <div className={styles.buttons}>{isEditing ? renderEditingButtons() : renderEditButton()}</div>
    </div>
  );

  const renderEditButton = (): React.JSX.Element => {
    if (isLocationEditor) {
      return <></>;
    }

    return (
      <Button startIcon={<ModeEditOutlineOutlinedIcon />} variant="text" onClick={handleOnEdit} key="editButton">
        {t('organisationSubscriptions.editFormButton')}
      </Button>
    );
  };

  const renderEditingButtons = (): React.JSX.Element => (
    <>
      <Button variant="text" onClick={handleOnCancel} key="cancelButton">
        {t('organisationSubscriptions.cancelButton')}
      </Button>
      <Button startIcon={<CheckRounded />} variant="contained" type="submit" key="saveButton">
        {t('organisationSubscriptions.saveButton')}
      </Button>
    </>
  );

  const renderDescription = (): React.JSX.Element => (
    <Typography variant="subtitle1" sx={{ color: Constants.Colors.onSurfaceVariant }}>
      {t('organisationSubscriptions.description')}
    </Typography>
  );

  const getToolTipText = (
    featureSubscriptionType: FeatureSubscriptionType,
    isDisabledByLocation: boolean,
    isDisabledByVcareConnector: boolean,
  ): string | undefined => {
    if (!isFeatureFlagForSubscriptionEnabled(featureSubscriptionType)) {
      return t('organisationSubscriptions.disabledByFeatureFlagTooltip');
    } else if (isDisabledByLocation) {
      return t('organisationSubscriptions.disabledByLocationTooltip');
    } else if (isDisabledByVcareConnector) {
      return t('organisationSubscriptions.disabledByVcareConnectorTooltip');
    }
    return undefined;
  };

  const renderSubscriptions = (): React.JSX.Element => {
    if (!readTenantFeatureSubscriptionsIsSuccess) {
      return <></>;
    }

    const isViewing = viewingMode === 'viewing';
    return (
      <div className={styles.subscriptions}>
        {readTenantFeatureSubscriptionsData?.tenantFeatureSubscriptions.map((subscription, index) => {
          const isDisabledByLocation = activeLocationFeatureSubscriptionTypes.has(subscription.featureSubscriptionType);
          const isDisabledByVcareConnector =
            subscription.featureSubscriptionType === FeatureSubscriptionType.CareConnect &&
            subscription.isActive &&
            (readTenantFeatureSubscriptionsData?.hasVcareConnector ?? false);
          return (
            <FeatureSubscriptionRow
              key={subscription.featureSubscriptionType}
              index={index}
              isActive={subscription.isActive}
              featureSubscriptionType={subscription.featureSubscriptionType}
              isViewing={isViewing}
              fieldNamePrefix="tenantFeatureSubscriptions"
              disabled={
                isDisabledByLocation ||
                isDisabledByVcareConnector ||
                !isFeatureFlagForSubscriptionEnabled(subscription.featureSubscriptionType)
              }
              tooltipText={
                isViewing
                  ? undefined
                  : getToolTipText(subscription.featureSubscriptionType, isDisabledByLocation, isDisabledByVcareConnector)
              }
            />
          );
        })}
      </div>
    );
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitTenantFeatureSubscriptions)} className={styles.container}>
        {renderHeaderRow()}
        {renderDescription()}
        {renderSubscriptions()}
      </form>
    </FormProvider>
  );
};

export default TenantFeatureSubscriptions;
